import React, { useMemo, useRef, useState } from 'react';
import { Button, DialogActions, DialogContent, DialogProps, 
    Tooltip, PaperProps, List, ListItem, ListItemText, Box, 
    ListItemButton,
    Typography,
    Divider,
    Paper,
    ListItemIcon,
    IconButton, 
    ToggleButton} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SpinnerButton from './SpinnerButton';
import { AssignmentInfo, Client, CustomFields, Gender, Patient, 
    PatientStatus, PatientTrackables, Plan, TrackableMeasure, Visit, VisitStatus,
    PatientCollection } from '../models/core';
import { API } from '../utils/Api';
import PersonIcon from '@mui/icons-material/Person';
import WarningIcon from '@mui/icons-material/Warning';
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined';
import useSnackbar from '../hooks/useSnackbar';
import AlertSnack from './ErrorSnack';
import { analytics } from '../utils/analytics/zipAnalytics';
import { useAuth } from '../hooks/useAuth';
import { DateTime } from 'luxon';
import HistoryIcon from '@mui/icons-material/History';
import AddIcon from '@mui/icons-material/Add';
import HistoryView from './patients/2_0/history/HistoryView';
import useDialog from '../hooks/useDialog';
import SimpleDialog from './dialogs/SimpleDialog';
import { fetchVisits } from '../utils/fetchUtils';
import PatientDocumentView from './patients/2_0/PatientDocumentView';
import FolderIcon from '@mui/icons-material/Folder';
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation';
import PatientInsuranceView from './patients/2_0/PatientInsuranceView';
import { UseQueryResult } from '@tanstack/react-query';
import { Insurance } from '../models/insurance';
import {isEqual} from 'lodash'
import { LoadingOverlay } from './LoadingOverlay';
import { VisitReasons } from '../utils/enums';
import ResizableDialog from './dialogs/ResizableDialog';
import { useInsuranceQuery } from '../queries/useInsuranceQuery';
import { findLastContactDate, formatDates, getNewestDate } from '../utils/visitUtils';
import ZCMPatientView from './patients/2_0/ZCMPatientView';
import { MeasureEditorViewSection, MeasureEditorViewState, patientTrackablesToState } from './patients/2_0/measures';
import PatientEditorView from './patients/2_0/PatientEditorView';


export interface UCMEditorDialogText {
    tooltip?: string;
    text: string;
}

export interface UCMEditorProps extends DialogProps {
    open: boolean;
    page?:'patient'|'visits'|'history'
    client: Client;
    plans: Plan[];
    patient?: Patient|undefined|null,
    canExpand?:boolean;
    visits?: Visit[],
    footer?: UCMEditorDialogText;
    readOnly?: boolean;
    submitText?: string;
    backdropClose?: boolean;
    dialogPaperProps?: PaperProps;
    limitedPrompt?:boolean;
    callSaveOnlyOnClose?:boolean;
    onClose?: () => void;
    onSave?: (collection:PatientCollection) => void;
}

function getVisitChanges(visit:Partial<Visit>) {
    if(visit) {
        const cf = visit['customFields'];
        return (cf && Object.keys(cf)?.join(',') || '') + (Object.keys(visit)?.filter((v) => v != 'customFields')?.join(',') || '');
    }
    return '';
}

const TooltipSpan = ({ title, children }: { title?:string, children?: React.ReactNode }):JSX.Element => {
    return (<Tooltip title={title}><span>{children}</span></Tooltip>)
}

const UCMEditor = ({
    open,
    page,
    canExpand,
    client,
    plans,
    patient,
    visits,
    footer,
    readOnly,
    onClose,
    onSave,
    submitText,
    backdropClose = true,
    limitedPrompt = false,
    callSaveOnlyOnClose = false,
    dialogPaperProps,
    ...props
}: UCMEditorProps): JSX.Element => {
    const [isPanelExpanded, setIsPanelExpanded] = useState(true);
    const [isAdminInfoOpen, setIsAdminInfoOpen] = useState(false);

    const togglePanel = () => { setIsPanelExpanded(!isPanelExpanded); };

    const MAX_RETRY_ATTEMPTS = 3;
    const [saving, setSaving] = useState(false);
    const [selectedKey, _setSelectedKey] = useState<keyof typeof UCMListItem>(page || 'patient');
    
    const [patientChangesText, setPatientChangesText] = useState<string>('');
    const [activePatient, setActivePatient] = useState<Patient|null>(patient || null);
    const activePatientId = useMemo(() => { return activePatient?._id || ''}, [activePatient?._id]);
    const [activePatientChanges, setActivePatientChanges] = useState<Partial<Patient>>({});
    const [unsyncedPatientChanges, setUnsyncedPatientChanges] = useState<Partial<Patient>>({});
    
    const [visitList, setVisitList] = useState(visits || null);
    const [visitChanges, setVisitChanges] = useState<Record<string,Partial<Visit>>>({});
    const [activeVisit, setActiveVisit] = useState(visits && visits.length > 1 ? visits[0] : null);
    const [unsyncedVisitChanges, setUnsyncedVisitChanges] = useState<Record<string, Partial<Visit>>>({});

    const [activeTrackables, setActiveTrackables] = useState<PatientTrackables>();
    const [syncedTrackableState, setSyncedTrackableState] = useState<MeasureEditorViewState>();
    const [unsyncedTrackableState, setUnsyncedTrackableState] = useState<MeasureEditorViewState>();

    const [contentLoading, setContentLoading] = useState(false);
    const [patientErrorStatus, setPatientErrorStatus] = useState<boolean>(false);
    const [pendingSnackError, setPendingSnackError] = useState<Error>();
    const [didSave, setDidSave] = useState<boolean>(false);
    const [creatingVisit, setCreatingVisit] = useState<boolean>(false);
    
    const [insuranceList, setInsuranceList] = useState<Insurance[] | undefined>(undefined);

    const contentPaperRef = useRef<HTMLDivElement>(null);
    const confirmVisitDialog = useDialog();
    const confirmFullSave = useDialog();
    const confirmCreatePatient = useDialog();
    const saveVisitListRef = useRef<Visit[]>([]);

    const { showError: snackError, showSuccess:snackSuccess, ...snackProps } = useSnackbar();
    React.useEffect(() => {
        if(pendingSnackError) { snackError(pendingSnackError); setPendingSnackError(undefined) }
    }, [pendingSnackError, snackError])

    const auth = useAuth();

    const setSelectedKey = React.useCallback((key:(keyof typeof UCMListItem)) => {
        if (contentPaperRef.current) {
            contentPaperRef.current.scrollTop = 0;
        }
        _setSelectedKey(key);
        setContentLoading(true);
    }, [])

    const [triggerFetchVisits, setTFV] = useState(false);

    React.useEffect(() => {
        _setSelectedKey(page || 'patient');
        setActivePatient(patient || null);

        setVisitList(visits || null);
        setUnsyncedPatientChanges({});
        setUnsyncedVisitChanges({});
        setActiveVisit(visits && visits.length > 0 ? visits[0] : null);
        setActiveTrackables(undefined);
        setUnsyncedTrackableState(undefined);
        setSyncedTrackableState(undefined);
        setCreatingVisit(false);
        setInsuranceList(undefined);

        if(page == 'visits' && !visits || visits?.length == 0) {
            setTFV(true);
        }
        
    },[page, client, patient, visits])

    const onFetchVisit = React.useCallback((visits?:Visit[], error?:string, newId?:string) => {
        if(visits && !error) {
            setVisitList(visits)
            setActiveVisit((prev) => !visits || visits.length == 0 ? null 
                : visits.find((v) => v._id == newId || prev?._id) || visits[0]);
        } else {
            setPendingSnackError(new Error('Failed to retrieve Visits, try again in a few minutes'));
            analytics.error('api_error', {
                message:'failed to retrieve visits. Max retries hit.', clientId:client._id, patientId:activePatientId
            }, true);
        }
    }, [activePatientId, client._id]);

    React.useEffect(() => {
        if(triggerFetchVisits) {
            setTFV(false);
            if(activePatientId != '') {
                fetchVisits(activePatientId,client._id, onFetchVisit);
            }
        }
    }, [triggerFetchVisits, activePatientId, client._id, onFetchVisit])

    const fetchTrackables = React.useCallback(async (pid:string, clientId:string) => {
        let retries = 0;
        while(retries < MAX_RETRY_ATTEMPTS) {
            try{
                const result = await API.getPatientTrackables(clientId, pid);
                if(result) {
                    if(result.items && result.items.length > 0) {
                        const state = result.items[0] || {};
                        setActiveTrackables(state)
                        setSyncedTrackableState(patientTrackablesToState(state));
                    } else {
                        setActiveTrackables({} as PatientTrackables)
                        setSyncedTrackableState({measuresUpdated:false,  diagnosisUpdated:false, chronicUpdated:false, 
                            suspectUpdated:false, chronic:[], diagnosis:[], suspect:[], measures:[] });
                    }
                    break;
                }
            } catch(e) {
                console.error(e)
            }
            retries += 1;
        }
        if(retries >= MAX_RETRY_ATTEMPTS) {
            //  ERROR
            setPendingSnackError(new Error('Failed to retrieve Trackables, try again in a few minutes'));
            analytics.error('api_error', { message:'failed to retrieve trackables. Max retries hit.', clientId:clientId, patientId:pid}, true);
        }
    }, [setActiveTrackables])

    const setTrackableCallback = React.useCallback((state:MeasureEditorViewState) => {
        setUnsyncedTrackableState(state);
    }, [])

    const onPatientStateChange = React.useCallback((hasErrors:boolean, _patient:Patient, changes:Partial<Patient>) => {
        setPatientErrorStatus(hasErrors);
        setUnsyncedPatientChanges(changes);
    },[]);

    const onVisitStateChange = React.useCallback((visit:Visit, change:Partial<Visit>) => {
        setUnsyncedVisitChanges((prev) => ({ ...prev, [visit._id]: { ...(prev[visit._id] || {}), ...change, } }));
    },[])
    
    const syncPendingVisitChanges = React.useCallback((id:string, change?:Partial<Visit>) => {
        if(change) {
            setVisitChanges((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...change, } }));
        }
    }, []);

    const [lastInsuranceUpdateTime, setLastInsuraceUpdateTime] = useState(0);

    const insuranceQuery = useInsuranceQuery(client._id, activePatient?._id || '', false);
    React.useEffect(() => {
        if(insuranceQuery.dataUpdatedAt != lastInsuranceUpdateTime) {
            setInsuranceList(insuranceQuery.data||[]);
            setLastInsuraceUpdateTime(insuranceQuery.dataUpdatedAt);
        }
    },[insuranceQuery.data, lastInsuranceUpdateTime])
    const didInsuranceChange = useMemo(() => {
        const newIL = insuranceQuery.data || [];
        if(!insuranceList) {
            return false;            
        }
        if(insuranceList?.length != newIL.length || newIL.length == 0) {
            return true;
        }
        let index = 0;
        for(const il of newIL) {
            const v = insuranceList[index];
            if(il.created !== v.created || il.deleted_time !== v.deleted_time || il.groupId !== v.groupId
                || il.memberId !== v.memberId || il.memberFirstName !== v.memberFirstName
                || il.memberLastName !== v.memberLastName || il.policyHolder !== v.policyHolder) {
                return true;
            }
            index += 1;
        }
        return false;
    }, [insuranceList, insuranceQuery.data]);

    const onTriggerPatientChange = (pt: Patient): void => {
        const changes = { ...unsyncedPatientChanges, zcmId:pt.zcmId};
        onPatientStateChange(false, pt, changes);
        setActivePatient(pt);
        setDidSave(true);
    }

    const patientZcmID = React.useMemo(() => {
        return patient?.zcmId ? Number.parseInt(patient?.zcmId || '0') : 0;},
    [patient?.zcmId]);

    // Example key-value pairs for the list
    const UCMListItem = {
        patient: () => { return <PatientEditorView patient={activePatient} client={client} plans={plans}
            patientState={activePatientChanges} onUpdateState={onPatientStateChange}
                triggerPatientChange={onTriggerPatientChange}
            />},
        insurance:() => {
            return  (!insuranceQuery.isFetched || insuranceQuery.isLoading)
                ? <></> : <PatientInsuranceView patient={activePatient||undefined}
                    insurance={insuranceList||[]} onUpdate={setInsuranceList} />
        },
        visits: () => {
            const VisitEditorView = React.lazy(() => import('./patients/2_0/VisitEditorView').then(module => ({ default: module.default })));
            return activeVisit && activePatient ? <React.Suspense fallback={<>Loading...</>}>
                <VisitEditorView visit={activeVisit}
                    client={client} patient={activePatient} visitState={visitChanges[activeVisit._id]}
                    onStateChange={onVisitStateChange} triggerPatientChange={onTriggerPatientChange}/>
            </React.Suspense> : <></>
        },
        conditions: () => {
            const MeasureEditorView = React.lazy(() => import('./patients/2_0/measures/MeasureEditorView').then(module => ({ default: module.default })));
            return syncedTrackableState || unsyncedTrackableState ? <React.Suspense fallback={<>Loading...</>}>
                <MeasureEditorView title='Conditions'
                    sections={[
                        MeasureEditorViewSection.Demographics,
                        MeasureEditorViewSection.Chronic,
                        MeasureEditorViewSection.Suspect,
                        MeasureEditorViewSection.Diagnosis,
                    ]}
                    initialState={syncedTrackableState}
                    onStateUpdate={setTrackableCallback}
                />
            </React.Suspense> : <></>
        },
        measures: () => {
            const MeasureEditorView = React.lazy(() => import('./patients/2_0/measures/MeasureEditorView').then(module => ({ default: module.default })));
            return syncedTrackableState || unsyncedTrackableState ? <React.Suspense fallback={<>Loading...</>}>
                <MeasureEditorView
                    title='Measures'
                    sections={[
                        MeasureEditorViewSection.Demographics,
                        MeasureEditorViewSection.Measures,
                    ]}
                    initialState={syncedTrackableState}
                    onStateUpdate={setTrackableCallback}
                />
            </React.Suspense> : <></>
        },
        documents: () =>{return <PatientDocumentView patient={activePatient||{} as Patient} client={client}/> },
        history: () => {return <HistoryView patient={activePatient||{} as Patient} /> },
    };

    const syncChanges = () => {
        unsyncedTrackableState && setSyncedTrackableState(unsyncedTrackableState);
        activeVisit && syncPendingVisitChanges(activeVisit._id, unsyncedVisitChanges[activeVisit._id])
        unsyncedPatientChanges && setActivePatientChanges(unsyncedPatientChanges);
    };

    const onSaveChangesClick = async (save?:(collection:PatientCollection)=>void) => {
        try {
            syncChanges();
            setSaving(true);
            const vxc:Record<string,Partial<Visit>> = {};
            visitList?.forEach((v) => { vxc[v._id] = { ...(visitChanges[v._id] || {}), ...(unsyncedVisitChanges[v._id] || {}) }; })

            const updatedPatient = await doFullSave(client._id, activePatient || null, unsyncedPatientChanges || activePatientChanges,
                visitList || [], vxc, unsyncedTrackableState || syncedTrackableState || null, {prev:insuranceList || [], query:insuranceQuery});
            if (updatedPatient) {
                save && save(
                    {
                        id:updatedPatient?._id || activePatient?._id,
                        patient:updatedPatient || activePatient||{} as Patient,
                        visits:saveVisitListRef.current || visitList || [],
                        rosters: undefined,
                        trackables:undefined
                    }
                )
            }
            setDidSave(true);
        } finally {
            setSaving(false);
        }
    }

    const onSaveAndContinue = async (save?:()=>void) => {
        await onSaveChangesClick((collection:PatientCollection) => {
            if(!callSaveOnlyOnClose && onSave) {
                onSave(collection);
                setDidSave(false);
            }
            save && save();
        });
    };

    const onSaveAndClose = async () => {
        await onSaveChangesClick((collection:PatientCollection) => {
            onSave && onSave(collection);
            onClose && onClose();
        });
    };

    const dialogButtonClose = () => {
        if(onSave && didSave) {
            onSave({ id:activePatient?._id, patient:activePatient||{} as Patient, visits:saveVisitListRef.current || visitList || []});
        }
        onClose && onClose();
    }

    const handleDialogClose = (_event: object, reason: "backdropClick" | "escapeKeyDown") => {
        if (!backdropClose && reason === "backdropClick") return;
        dialogButtonClose();
    };

    const handleListItemClick = async (key: keyof typeof UCMListItem, newEntityId?:string) => {

        if(selectedKey == key && !newEntityId || !activePatient) {
            return;
        }

        if(selectedKey == 'measures' || selectedKey == 'conditions') {
            unsyncedTrackableState && setSyncedTrackableState(unsyncedTrackableState);
        } else if(selectedKey == 'visits') {
            activeVisit && syncPendingVisitChanges(activeVisit._id, unsyncedVisitChanges[activeVisit._id])
        } else if(selectedKey == 'patient') {
            setActivePatientChanges(unsyncedPatientChanges);
        }

        if(key == 'insurance' && insuranceList === undefined) {
            insuranceQuery.refetch();
        }

        setSelectedKey(key);

        if(key == 'visits') {
            if(!visitList || visitList.length == 0) {
                await fetchVisits(activePatient?._id || '', client._id, (v, e) => { onFetchVisit(v,e, newEntityId); }
            );
            } else if(!activeVisit && !newEntityId) {
                setActiveVisit(visitList[0]);
            } else if(newEntityId) {
                setActiveVisit(visitList?.find((c) => c._id == newEntityId)
                    || saveVisitListRef.current?.find((c) => c._id == newEntityId) || null);
            }
        } else if(key == 'measures' || key == 'conditions') {
            if(!activeTrackables) {
                fetchTrackables(activePatient?._id || '', client._id);
            }
        }
    };

    const patientHasChanges = React.useMemo(() => {
        const cm = unsyncedPatientChanges;
        if(!cm) {
            return false;
        }
        const ck = Object.keys(cm);
        let changeKeys = '';
        if(ck) {
            changeKeys = ck.join(',') || '';
        }
        setPatientChangesText(changeKeys);
        return changeKeys.length > 0;
    }, [unsyncedPatientChanges]);

    const visitHasChanged = React.useCallback((visitId:string) => {
        const cm = unsyncedVisitChanges[visitId] || visitChanges[visitId];
        if(cm) {
            const cf = cm['customFields'];
            if(cf) {
                if(Object.keys(cf)?.length > 0) {
                    return true
                }
            }
            const cmKeys = Object.keys(cm);
            return cmKeys?.length > 1;
        }
        return false;
    }, [visitChanges, unsyncedVisitChanges]);

    React.useEffect(() => {
        const interval = setInterval(() => {
          setContentLoading(false);
        }, 500);
        return () => clearInterval(interval);
    }, [contentLoading, selectedKey]);

    const createBlankVisit = React.useCallback(() => {
        return {
            status: VisitStatus.NEW, language: 'English', scheduled1: undefined, scheduled2: undefined,
            scheduled3: undefined, scheduled4: undefined, scheduledReason1: undefined, scheduledReason2: undefined,
            scheduledReason3: undefined, scheduledReason4: undefined, comments: undefined, clientNotes: undefined, clientDataEntry: undefined,
            appointmentId: undefined, customFields: undefined, numCalls: 0, numEmails: 0, numSMS: 0, numMails: 0,
            numContacts: 0, numReschedules : 0, refusalReason: undefined, phoneDate: undefined, emailDate: undefined, utrDate: undefined,
            scheduledDate: undefined, phoneOutcome: undefined, emailOutcome: undefined, appointmentOutcome: undefined, notes: undefined,
            memberInfoUpdate: undefined, lastContactDate: 'unknown'
        }
    }, [])

    const saveVisit = React.useCallback(async (patientId:string, clientId:string, currentVisit:Visit|undefined|null, visiChanges:Partial<Visit>) => {
        if(!visiChanges) {
            console.error('No Changes Defined for Visit: ' + currentVisit?._id || 'unknown');
            return null;
        }

        function getValue(key:keyof Visit, defaultValue:undefined|string|string[]|number|CustomFields|AssignmentInfo|boolean) {
            return visiChanges[key] !== undefined ? visiChanges[key] : defaultValue;
        }

        function fallbackString(value?:string) {
            return value ? '' : undefined;
        }

        const changeKeys = Object.keys(visiChanges || {}) || [];
        const lastChanges = changeKeys.length > 0 ? changeKeys.reduce((p, c) => `${p+','+c}`) : '';
        const customFields = {...currentVisit?.customFields, ...(getValue('customFields', {} as CustomFields) as CustomFields) };
        const lastContact = findLastContactDate(customFields);

        const stp = getValue('sentToPCP', currentVisit?.sentToPCP) as boolean || false;

        const scheduled1 = getValue('scheduled1', currentVisit?.scheduled1) as string || undefined;
        const scheduled2 = getValue('scheduled2', currentVisit?.scheduled2) as string || undefined;
        const scheduled3 = getValue('scheduled3', currentVisit?.scheduled3) as string || undefined;
        const scheduled4 = getValue('scheduled4', currentVisit?.scheduled4) as string || undefined;

        const newVisit = {
            status: getValue('status', currentVisit?.status + '') as VisitStatus,
            language: getValue('language', currentVisit?.language) as string || undefined,
            scheduled1: scheduled1 || '',
            scheduled2: scheduled2 || '',
            scheduled3: scheduled3 || '',
            scheduled4: scheduled4 || '',
            scheduledReason1: getValue('scheduledReason1', currentVisit?.scheduledReason1) as string || fallbackString(currentVisit?.scheduledReason1),
            scheduledReason2: getValue('scheduledReason2', currentVisit?.scheduledReason2) as string || fallbackString(currentVisit?.scheduledReason2),
            scheduledReason3: getValue('scheduledReason3', currentVisit?.scheduledReason3) as string || fallbackString(currentVisit?.scheduledReason3),
            scheduledReason4: getValue('scheduledReason4', currentVisit?.scheduledReason4) as string || fallbackString(currentVisit?.scheduledReason4),
            comments: getValue('comments', currentVisit?.comments) as string || fallbackString(currentVisit?.comments),
            sentToPCP: stp,
            clientNotes: getValue('clientNotes', currentVisit?.clientNotes) as string || fallbackString(currentVisit?.clientNotes),
            clientDataEntry: getValue('clientDataEntry', currentVisit?.clientDataEntry) as string || fallbackString(currentVisit?.clientDataEntry),
            appointmentId: getValue('appointmentId', currentVisit?.appointmentId) as number || undefined,
            customFields: formatDates(customFields),
            numCalls: getValue('numCalls', currentVisit?.numCalls) as number || 0,
            numEmails: getValue('numEmails', currentVisit?.numEmails) as number || 0,
            numSMS: getValue('numSMS', currentVisit?.numSMS) as number || 0,
            numMails: getValue('numMails', currentVisit?.numMails) as number || 0,
            numContacts: getValue('numContacts', currentVisit?.numContacts) as number || 0,
            numReschedules: getValue('numReschedules', currentVisit?.numReschedules) as number || 0,
            refusalReason: getValue('refusalReason', currentVisit?.refusalReason) as string || fallbackString(currentVisit?.refusalReason),
            phoneDate: getValue('phoneDate', currentVisit?.phoneDate) as string || fallbackString(currentVisit?.phoneDate),
            emailDate: getValue('emailDate', currentVisit?.emailDate) as string || fallbackString(currentVisit?.emailDate),
            utrDate: getValue('utrDate', currentVisit?.utrDate) as string || fallbackString(currentVisit?.utrDate),
            scheduledDate: getNewestDate([
                DateTime.fromISO(scheduled1||''),
                DateTime.fromISO(scheduled2||''),
                DateTime.fromISO(scheduled3||''),
                DateTime.fromISO(scheduled4||'')])?.toISODate() || undefined,
            phoneOutcome: getValue('phoneOutcome', currentVisit?.phoneOutcome) as string || fallbackString(currentVisit?.phoneOutcome),
            emailOutcome: getValue('emailOutcome', currentVisit?.emailOutcome) as string || fallbackString(currentVisit?.emailOutcome),
            appointmentOutcome: getValue('appointmentOutcome', currentVisit?.appointmentOutcome) as string || fallbackString(currentVisit?.appointmentOutcome),
            notes: getValue('notes', currentVisit?.notes) as string || fallbackString(currentVisit?.notes),
            memberInfoUpdate: getValue('memberInfoUpdate', currentVisit?.memberInfoUpdate) as string || fallbackString(currentVisit?.memberInfoUpdate),
            visitReasons: getValue('visitReasons', currentVisit?.visitReasons) as string[] || [],
            lastContactDate: lastContact?.toISO({ suppressMilliseconds: true, suppressSeconds: true, includeOffset: false }) || 'unknown'
        };
        analytics.track(currentVisit ? 'visit_update' : 'visit_create', {
            clientId: clientId,
            patientId: patientId,
            changes: lastChanges
        }, true);

        let vid:string|undefined = currentVisit?._id;
        const visitState = { ...(currentVisit as Visit), ...newVisit };
        console.log(visitState);

        if (currentVisit) {
            const updatedV = await API.updateVisit(visitState);
            if(!updatedV) {
                return null;
            }
        }
        else {
            const {acknowledged:_ack, insertedId } = await API.createVisit({ clientId, patientId, 
                refId:`visit-${patientId}-${(visitList?.length || 0) + 1}`, ...newVisit });
            if(insertedId) {
                vid = insertedId;
            }
        }

        if(vid) {
            setUnsyncedVisitChanges((prev) => ({ ...prev, [vid as string]: {} }));
            setVisitChanges((prev) => ({ ...prev, [vid as string]: {} }));
            const vlist = (visitList?.filter((v) => v._id != vid) || []);
            vlist.push({ ...visitState, _id:vid });

            saveVisitListRef.current = vlist;
            setVisitList(vlist);
            setActiveVisit((prev) => prev?._id == vid ? visitState : prev);
        }
        return vid;
    },[visitList]);

    const createVisit = React.useCallback(async (patientId:string, clientId:string, onCreated?:(id:string) => Promise<void>|void) => {
        const vs = createBlankVisit() as Partial<Visit>;
        const vid = await saveVisit(patientId, clientId, null, vs);
        await fetchVisits(patientId, clientId, onFetchVisit);
        //console.log('vid: ' + vid);
        if(vid && onCreated) {
            await onCreated(vid);
        }
    }, [saveVisit, createBlankVisit, onFetchVisit])


    const savePatient = React.useCallback(async (currentPatient:Patient|undefined|null, changes:Partial<Patient>) => {
        if(!patientHasChanges || patientErrorStatus) {
            setPendingSnackError(new Error('Errors in Patients Data. Fix the Errors before Saving.'));
            return null;
        }

        let pid = currentPatient?._id || '';
        function getValue(key:keyof Patient, defaultValue:undefined|string|string[]|number|CustomFields|AssignmentInfo) {
            return changes[key] ? changes[key] : defaultValue;
        }

        function getUpdatedPatientFields(newPatient: Partial<Patient>, currentPatient:Readonly<Patient>): Partial<Patient> {
            const updatedFields: Record<string, unknown> = {};

            for (const key in newPatient) {
                if (Object.prototype.hasOwnProperty.call(newPatient, key)) {
                    const typedKey = key as keyof Patient;
                    const newValue = newPatient[typedKey];
                    const currentValue = currentPatient[typedKey];

                    // Treat undefined/null as equivalent OR compare directly
                    const areEqual =
                        (newValue === undefined || newValue === null) &&
                        (currentValue === undefined || currentValue === null)
                            ? true : newValue === currentValue;

                    if (!areEqual) {
                        updatedFields[typedKey] = newValue;
                    }
                }
            }

            return updatedFields;
        }

        const refId = getValue('refId', currentPatient?.refId) as string || '';

        let invalidPatient = refId == '';
        if (!currentPatient && !invalidPatient) {
            invalidPatient = (await API.getPatients(client._id, [['refId',refId]]))?.items?.length != 0;
            if(invalidPatient) {
                setPendingSnackError(new Error(`${refId} already exists for practice ${client.practiceId}`));
                return null;
            }
        }

        const assign = getValue('assignment', currentPatient?.assignment) as AssignmentInfo || { assignedBy:'', assignedDate:'', assignedTo:'None'};
        if(assign?.assignedTo != currentPatient?.assignment?.assignedTo) {
            analytics.track('assign', {  type:'single', currentPatient:currentPatient?._id || '',
                to:assign?.assignedTo || '',  by: auth?.user?.name || 'NA'
            }, true);
        }

        const customFields = {...currentPatient?.customFields, ...(getValue('customFields', {} as CustomFields) as CustomFields) };

        const newPatient = {
            refId: refId,
            status: getValue('status', currentPatient?.status ? currentPatient?.status + '' : undefined ) as PatientStatus || PatientStatus.ACTIVE,
            medicaidId: getValue('medicaidId', currentPatient?.medicaidId) as string,
            medicareId: getValue('medicareId', currentPatient?.medicareId) as string,
            lineOfBusiness: getValue('lineOfBusiness', currentPatient?.lineOfBusiness) as string,
            firstName: getValue('firstName', currentPatient?.firstName) as string,
            lastName: getValue('lastName', currentPatient?.lastName) as string,
            gender: getValue('gender', currentPatient?.gender ? currentPatient?.gender + '' : undefined) as Gender || undefined,
            dob: getValue('dob', currentPatient?.dob) as string,
            address: getValue('address', currentPatient?.address) as string,
            address2: getValue('address2', currentPatient?.address2) as string,
            city: getValue('city', currentPatient?.city) as string,
            county: getValue('county', currentPatient?.county) as string,
            state: getValue('state', currentPatient?.state) as string,
            zip: getValue('zip', currentPatient?.zip) as string,
            phone: getValue('phone', currentPatient?.phone) as string,
            phone2: getValue('phone2', currentPatient?.phone2) as string,
            email: getValue('email', currentPatient?.email) as string,
            guardianName: getValue('guardianName', currentPatient?.guardianName) as string,
            guardianPhone: getValue('guardianPhone', currentPatient?.guardianPhone) as string,
            guardianEmail: getValue('guardianEmail', currentPatient?.guardianEmail) as string,
            subProgram: getValue('subProgram', currentPatient?.subProgram) as string,
            pcpName: getValue('pcpName', currentPatient?.pcpName) as string,
            pcpPhone: getValue('pcpPhone', currentPatient?.pcpPhone) as string,
            pcpNPI: getValue('pcpNPI', currentPatient?.pcpNPI) as string,
            practiceName: getValue('practiceName', currentPatient?.practiceName) as string,
            groupName: getValue('groupName', currentPatient?.groupName) as string,
            caseManager: getValue('caseManager', currentPatient?.caseManager) as string,
            caseManagerPhone: getValue('caseManagerPhone', currentPatient?.caseManagerPhone) as string,
            language: getValue('language', currentPatient?.language) as string,
            risk: getValue('risk', currentPatient?.risk) as number,
            riskCategory: getValue('riskCategory', currentPatient?.riskCategory) as string,
            customFields: customFields,
            assignment: assign,
        };

        let returnPatient = null;
        if (currentPatient && pid && pid != '') {

            const patientChanges = currentPatient ? getUpdatedPatientFields(newPatient, currentPatient) : newPatient;
            const fullChanges = { ...currentPatient, ...newPatient, };
            const mpat = { ...patientChanges,
                _id:currentPatient._id,
                customFields:customFields,
                assignment:assign,
                clientId:currentPatient.clientId
            };
            const rest = await API.updatePatient(mpat);

            if(!rest) {
                setPendingSnackError(new Error('Failed to save patient, try again in a few minutes.'));
                analytics.error('patient_update_error', { clientId: client._id, }, true);
                return null;
            }
            analytics.track('patient_update', { clientId: client._id, patientId: pid, refId: refId }, true)
            setActivePatient(fullChanges);
            returnPatient = fullChanges;
        } else {
            const { acknowledged, insertedId } = await API.createPatient({ clientId: client._id, ...newPatient });
            if(!acknowledged) {
                setPendingSnackError(new Error('Failed to create patient, try again in a few minutes.'));
                analytics.error('patient_create_error', { clientId: client._id, }, true);
                return null;
            }
            pid = insertedId;
            analytics.track('patient_create', { clientId: client._id, patientId: pid, refId: refId }, true)

            await createVisit(pid, client._id);

            returnPatient = { _id:pid, clientId: client._id, ...newPatient };
            setActivePatient(returnPatient);
        }
        setUnsyncedPatientChanges({});
        setActivePatientChanges({});
        setDidSave(true);
        return returnPatient;
    },[patientHasChanges, patientErrorStatus, auth?.user, client, createVisit])

    const saveVisitChangeChecked = React.useCallback(async (currentVisit:Visit|undefined|null, visitChanges:Partial<Visit>, patientId:string, clientId:string) => {
        console.log(visitChanges);
        if(!patientId) {
            setPendingSnackError(new Error('No Valid Patient Found'));
            analytics.error('save_visit_no_patient', { message:'No valid patient found', clientId:clientId, }, true);
            return;
        }
        if(!visitHasChanged(currentVisit?._id || '')) {
            setPendingSnackError(new Error('No Changes Made or Error in the Visits Data'));
            analytics.error('save_visit_no_visit', { message:'No changes or invalid visit', clientId:clientId, patientId:patientId }, true);
            return;
        }
        await saveVisit(patientId, clientId, currentVisit, visitChanges);
        setDidSave(true);
    }, [saveVisit, visitHasChanged]);

    const saveTrackables = React.useCallback(async (patientId:string, clientId:string, trackables:MeasureEditorViewState) => {
        if(!trackables || (!trackables?.chronicUpdated && !trackables?.diagnosisUpdated && !trackables.measuresUpdated && !trackables.suspectUpdated)) {
            return;
        }

        const trackableElements:TrackableMeasure[] = [
            ...(trackables?.chronic || []),
            ...(trackables?.diagnosis || []),
            ...(trackables?.measures || []),
            ...(trackables?.suspect || [])
        ];

        const tid:string|undefined = activeTrackables?._id;
        if(activeTrackables) {
            if(tid) {
                await API.updatePatientTrackables({ ...activeTrackables, ...{trackable:trackableElements }});
                analytics.track('trackables_update', {  patientId:patientId || '' }, true);
            } else {
                await API.createPatientTrackables({ name:patientId, patientId:patientId,
                    clientId:clientId, trackable:trackableElements});
                analytics.track('trackables_create', {  patientId:patientId || '' }, true);
            }
        }
        setDidSave(true);
        setUnsyncedTrackableState((prev) => 
            ({ ...prev, ...{ chronicUpdated:false, diagnosisUpdated:false, suspectUpdated:false, measuresUpdated:false} as MeasureEditorViewState})
        );
    },[activeTrackables]);

    function getChangedFields<T extends object>(original: T, updated: T): Partial<T> {
        const changedFields: Partial<T> = {};
            (Object.keys(updated) as (keyof T)[]).forEach(key => {
                if (original[key] !== updated[key]) {
                    changedFields[key] = updated[key];
                }
            });
        return changedFields;
    }

    const saveInsurance = React.useCallback(async (insuranceList:Insurance[], prev:Insurance[],
            patientId:string, clientId:string, refresh:()=>void) => {
        const newInsurance = insuranceList.filter((i) => !i._id || i._id == '')?.map((i) => { 
            return { ...i, patientId:patientId, clientId:clientId }
        });

        console.log(insuranceList);
        console.log(prev);

        const deletedInsurance = prev.filter((p) => !insuranceList.find(i => i._id && i._id === p._id));
        const updateInsuance = insuranceList.filter((p) => prev.find(i => (i._id && i._id == p._id) ? !isEqual(i, p) : undefined));
        console.log('New:', newInsurance);
        console.log('Deleted:', deletedInsurance);
        console.log('Updated:', updateInsuance);

        //  New new insurance
        for(const insurance of newInsurance) {
            await API.createInsurance(clientId, patientId, insurance);
        }

        // Delete the insurance
        for(const insurance of deletedInsurance) {
            if(insurance._id) {
                await API.deleteInsurance(clientId, patientId, insurance._id);
            }
        }

        // Update the insurance
        for(const insurance of updateInsuance) {
            if(insurance._id) {
                const initial = prev.find(i => i._id && i._id == insurance._id);
                if(!initial) {   continue;   }
                const upInsFields = { ...getChangedFields(initial, insurance), _id:insurance._id };
                await API.updateInsurance(clientId, patientId, upInsFields);
            }
        }
        refresh && refresh();
    }, []);


    const doFullSave = React.useCallback(async (clientId:string, patient:Patient|null, patientChanges:Partial<Patient>, 
        visits:Visit[]|null, visitChanges:{[k:string]:Partial<Visit>}, trackables:MeasureEditorViewState|null, 
        insurance:{prev:Insurance[], query:UseQueryResult<Insurance[], Error>}) => {
        
        let patientId = patient?._id || '';
        let ptr = patient;
        if(patientChanges && Object.keys(patientChanges).length > 0) {
            ptr = await savePatient(patient, patientChanges);
            if(!ptr) {  return null;  }
            patientId = ptr._id;
        }

        if(patientId) {
            if(visitChanges && Object.keys(visitChanges).length > 0) {
                for (const ev of Object.entries(visitChanges)) {
                    const id = ev[0];
                    const vc = ev[1];
                    if(Object.keys(vc).length > 0) {
                        await saveVisit(patientId, clientId, visits?.find((v) => v._id === id), vc);
                    }
                }
            }

            if(trackables && (trackables?.chronicUpdated || trackables?.diagnosisUpdated 
                || trackables?.measuresUpdated || trackables?.suspectUpdated)) {
                await saveTrackables(patientId, clientId, trackables);
            }

            await saveInsurance(insurance.prev, insurance.query.data||[], 
                patientId, clientId, insurance.query.refetch);
        }
        return ptr;
    }, [saveVisit, savePatient, saveTrackables, saveInsurance]);

    return (
        <ResizableDialog {...props} canExpand={canExpand||true} open={open} title={'UCMEditor'} onClose={handleDialogClose}
            initialSettings={dialogPaperProps || { sx: { height:'90%', maxHeight:'90%', width: '80%', maxWidth: '80%' } }}
            banner={
                limitedPrompt ? <Box sx={{ width: '100%', height:'10%', backgroundColor:'#0088d6', borderBottom: '1px solid #ccc', padding: '2px' }}>
                    <Typography variant='body1' align="center" color={'#ffffff'}>
                        {'This is a limited ZiphyOutreach experience. The patient selected from ZiphyAdmin is the only patient that can be accessed in this view.'}
                        <br/>
                        {'To find another patient, return to ZiphyAdmin or access ZiphyOutreach from the front door.'}
                    </Typography>
                </Box> : undefined
            }
            actions={
                <ToggleButton
                    value="adminInfo"
                    selected={isAdminInfoOpen}
                    onChange={() => setIsAdminInfoOpen(prev => !prev)}
                    size='small'
                    sx={{
                        border: 'none',
                        borderRadius: '50%',
                        position: 'absolute',
                        top: 8,
                        right: 44,
                    }}
                    >
                    <img src="/logo64.png" alt="logo" style={{ width: 24, height: 24 }} />
                </ToggleButton>
            }
        >
            <DialogContent sx={{ display: 'flex', flexDirection: 'row' }}>
                <Box sx={{ width: isPanelExpanded ? '18%' : '50px', minWidth: isPanelExpanded ? '200px' : '50px', borderRight: '1px solid #ccc', padding: '2px', transition: 'width 0.1s' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: '4px' }}>
                        <IconButton onClick={togglePanel} size="small">
                            {isPanelExpanded ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                        </IconButton>
                    </Box>
                    <Paper sx={{ boxShadow: "none" }}
                        style={{minHeight:'100%', maxHeight:'100%', maxWidth:'100%', overflow: 'auto', padding: '1px'}}>
                    <List>
                        <ListItem key={'ll-patient'} onClick={async () => await handleListItemClick('patient')} style={{padding:'2px 2px 2px 2px'}}>
                            <ListItemButton disableRipple selected={selectedKey == 'patient'} sx={{padding:'4px', display:'flex', alignItems: 'center'}}>
                                <ListItemIcon sx={{ minWidth: '36px' }}><PersonIcon/></ListItemIcon>
                                {isPanelExpanded && (
                                    <ListItemText
                                        primary={
                                            <Typography
                                                variant='h5'
                                                component='div'
                                                sx={{
                                                    justifyContent: 'left',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    flexGrow: 1,
                                                    minWidth: 0
                                                }}
                                            >
                                                Patient
                                            </Typography>
                                        }
                                    />
                                )}
                                {isPanelExpanded && (
                                    <TooltipSpan title={ 'Save Changes' + (patientChangesText == '' ? '' : ` [${patientChangesText}]`) }>
                                        {
                                            patientHasChanges ? (
                                                <IconButton
                                                    sx={{
                                                        justifyContent:'right',
                                                        color:(patientHasChanges ? (patientErrorStatus ? 'red' : undefined) : 'transparent'),
                                                        flexShrink: 0
                                                    }}
                                                    onClick={() => { savePatient(activePatient, unsyncedPatientChanges) } }
                                                >
                                                    <WarningIcon/>
                                                </IconButton>
                                            ) : <></>
                                        }
                                    </TooltipSpan>
                                )}
                            </ListItemButton>
                        </ListItem>
                        <Divider/>
                        <ListItem key={'ll-insurance'} onClick={() => handleListItemClick('insurance')} style={{padding:'2px 2px 2px 2px'}}>
                            <ListItemButton disableRipple disabled={activePatient==null} selected={selectedKey == 'insurance'} sx={{padding:'4px', display:'flex', alignItems: 'center'}}>
                                <ListItemIcon sx={{ minWidth: '36px' }}><MedicalInformationIcon/></ListItemIcon>
                                {isPanelExpanded && (
                                    <ListItemText
                                        primary={
                                            <Typography
                                                variant='h5'
                                                component='div'
                                                sx={{
                                                    justifyContent: 'left',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    flexGrow: 1,
                                                    minWidth: 0 // Allow text to shrink
                                                }}
                                            >
                                                Insurance
                                            </Typography>
                                        }
                                    />
                                )}
                                {isPanelExpanded && didInsuranceChange ?
                                    <TooltipSpan title={ 'Save Changes'}>
                                        {
                                            <IconButton sx={{justifyContent:'right', flexShrink: 0 }}
                                                onClick={async () => {
                                                    await saveInsurance(insuranceList || [], insuranceQuery.data||[],
                                                        activePatient?._id ||'', client._id, insuranceQuery.refetch); } }>
                                                <WarningIcon/>
                                            </IconButton>
                                        }
                                    </TooltipSpan> : <></>
                                }
                            </ListItemButton>
                        </ListItem>
                        <Divider/>
                        <ListItem key={'ll-visits'} onClick={async () =>  {
                                if(activePatient) {
                                    if(selectedKey != 'visits') {   setContentLoading(true); }
                                    await handleListItemClick('visits');
                                }
                            }} style={{padding:'2px 2px 2px 2px'}}>
                            <ListItemButton disableRipple selected={selectedKey == 'visits'} sx={{padding:'4px', display:'flex', alignItems: 'center'}}
                                disabled={activePatient==null}>
                                <ListItemIcon sx={{ minWidth: '36px' }}><MedicalServicesOutlinedIcon/></ListItemIcon>
                                {isPanelExpanded && (
                                    <ListItemText
                                        primary={
                                            <Typography
                                                variant='h5'
                                                component='div'
                                                sx={{
                                                    justifyContent: 'left',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    flexGrow: 1,
                                                    minWidth: 0
                                                }}
                                            >
                                                Visits
                                            </Typography>
                                        }
                                    />
                                )}
                                {isPanelExpanded && (
                                    <span>
                                        {
                                            <IconButton sx={{justifyContent:'right', flexShrink: 0}}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    confirmVisitDialog.show();
                                                }}>
                                                <AddIcon/>
                                            </IconButton>
                                        }
                                    </span>
                                )}
                            </ListItemButton>
                        </ListItem>
                        <Divider/>
                        {
                            isPanelExpanded && visitList && visitList.map((v, idx) => {
                                const hasChanges = visitHasChanged(v._id);
                                const change = unsyncedVisitChanges[v._id] || visitChanges[v._id];
                                const changeString = getVisitChanges(change);
                                const visitReasons = ((v?._id && change && change['visitReasons'] !== undefined)
                                    ? change['visitReasons'] : v?.visitReasons) as string[] || [];
                                    
                                return <ListItem
                                    key={v._id}
                                    onClick={async () => {
                                        setContentLoading(true);
                                        await handleListItemClick('visits', v._id);
                                    }}
                                    style={{padding:'1px 4px 1px 4px'}}
                                    >
                                    <ListItemButton
                                        disableRipple
                                        selected={
                                            (selectedKey === 'visits' && activeVisit && activeVisit._id === v._id) || false
                                        }
                                        disabled={activePatient == null}
                                        sx={{borderTop:(idx != 0 ? 1 : 0) }}
                                    >
                                        <Box display="flex" flexDirection="column" width="100%">
                                        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                            <b>{`Status: ${v.status || ''}`}</b>
                                            <TooltipSpan title={`Save Changes [${changeString||''}]`}>
                                                {hasChanges && (
                                                <IconButton
                                                    onClick={(e) => {
                                                    e.stopPropagation();
                                                    activeVisit && syncPendingVisitChanges(
                                                        activeVisit?._id,
                                                        unsyncedVisitChanges[activeVisit?._id],
                                                        );
                                                    const changes = {
                                                        ...(visitChanges[v._id] || {}),
                                                        ...unsyncedVisitChanges[v._id],
                                                    };
                                                    saveVisitChangeChecked(
                                                        v,
                                                        changes,
                                                        activePatient?._id || '',
                                                        client._id,
                                                    );
                                                    }}
                                                    sx={{ color: hasChanges ? undefined : 'transparent' }}
                                                >
                                                    <WarningIcon />
                                                </IconButton>
                                                )}
                                            </TooltipSpan>
                                        </Box>
                                        <Box mt={0.5} fontSize="0.875rem" color="text.secondary">
                                            <div>
                                            <b>Reason:</b>{' '}
                                            {visitReasons
                                                .map(
                                                (code) =>
                                                    VisitReasons.find((c) => c.code === code)?.name || '',
                                                )
                                                .filter((e) => e !== '')
                                                .join(', ')}
                                            </div>
                                            <div>{v.created?.substring(0, v.created?.indexOf('T'))}</div>
                                        </Box>
                                        </Box>
                                    </ListItemButton>
                                </ListItem>
                            })
                        }
                        { visitList && visitList.length > 0 ? <Divider/> : <></> }
                        <ListItem key={'ll-conditions'} onClick={() => handleListItemClick('conditions')} style={{padding:'2px 2px 2px 2px'}}>
                            <ListItemButton disableRipple selected={selectedKey == 'conditions'} sx={{padding:'4px', display:'flex', alignItems: 'center'}}
                                disabled={activePatient==null}>
                                <ListItemIcon sx={{ minWidth: '36px' }}><PersonIcon/></ListItemIcon>
                                {isPanelExpanded && (
                                    <ListItemText
                                        primary={
                                            <Typography
                                                variant='h5'
                                                component='div'
                                                sx={{
                                                    justifyContent: 'left',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    flexGrow: 1,
                                                    minWidth: 0
                                                }}
                                            >
                                                Conditions
                                            </Typography>
                                        }
                                    />
                                )}
                                {isPanelExpanded && (
                                    <TooltipSpan title='Save Changes'>
                                        {
                                            unsyncedTrackableState?.diagnosisUpdated || unsyncedTrackableState?.suspectUpdated || unsyncedTrackableState?.chronicUpdated ?
                                            (
                                                <IconButton
                                                    sx={{
                                                        justifyContent:'right',
                                                        color:(unsyncedTrackableState?.diagnosisUpdated || unsyncedTrackableState?.suspectUpdated || unsyncedTrackableState?.chronicUpdated ? undefined : 'transparent'),
                                                        flexShrink: 0
                                                    }}
                                                    onClick={() => saveTrackables(patient?._id || '', client?._id, unsyncedTrackableState)}
                                                >
                                                    <WarningIcon/>
                                                </IconButton>
                                            ) : <></>
                                        }
                                    </TooltipSpan>
                                )}
                            </ListItemButton>
                        </ListItem>
                        <Divider/>
                        <ListItem key={'ll-measures'} onClick={() => handleListItemClick('measures')} style={{padding:'2px 2px 2px 2px'}}>
                            <ListItemButton disableRipple selected={selectedKey == 'measures'} sx={{padding:'4px', display:'flex', alignItems: 'center'}}
                                disabled={activePatient==null}>
                                <ListItemIcon sx={{ minWidth: '36px' }}><PersonIcon/></ListItemIcon>
                                {isPanelExpanded && (
                                    <ListItemText
                                        primary={
                                            <Typography
                                                variant='h5'
                                                component='div'
                                                sx={{
                                                    justifyContent: 'left',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    flexGrow: 1,
                                                    minWidth: 0
                                                }}
                                            >
                                                Measures
                                            </Typography>
                                        }
                                    />
                                )}
                                {isPanelExpanded && (
                                    <TooltipSpan title='Save Changes'>
                                        {
                                            unsyncedTrackableState?.measuresUpdated ?
                                            (
                                                <IconButton
                                                    sx={{
                                                        justifyContent:'right',
                                                        color:(unsyncedTrackableState?.measuresUpdated ? undefined : 'transparent'),
                                                        flexShrink: 0
                                                    }}
                                                    onClick={() => saveTrackables(patient?._id || '', client?._id, unsyncedTrackableState) }
                                                >
                                                    <WarningIcon/>
                                                </IconButton>
                                            ) : <></>
                                        }
                                    </TooltipSpan>
                                )}
                            </ListItemButton>
                        </ListItem>
                        <Divider/>

                        <ListItem key={'ll-documents'} onClick={() => handleListItemClick('documents')} style={{padding:'2px 2px 2px 2px'}}>
                            <ListItemButton disableRipple disabled={activePatient==null} selected={selectedKey == 'documents'} sx={{padding:'4px', display:'flex', alignItems: 'center'}}>
                                <ListItemIcon sx={{ minWidth: '36px' }}><FolderIcon/></ListItemIcon>
                                {isPanelExpanded && (
                                    <ListItemText
                                        primary={
                                            <Typography
                                                variant='h5'
                                                component='div'
                                                sx={{
                                                    justifyContent: 'left',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    flexGrow: 1,
                                                    minWidth: 0
                                                }}
                                            >
                                                Documents
                                            </Typography>
                                        }
                                    />
                                )}
                            </ListItemButton>
                        </ListItem>
                        <Divider/>

                        <ListItem key={'ll-history'} onClick={() => handleListItemClick('history')} style={{padding:'2px 2px 2px 2px'}}>
                            <ListItemButton disableRipple disabled={activePatient==null} selected={selectedKey == 'history'} sx={{padding:'4px', display:'flex', alignItems: 'center'}}>
                                <ListItemIcon sx={{ minWidth: '36px' }}><HistoryIcon/></ListItemIcon>
                                {isPanelExpanded && (
                                    <ListItemText
                                        primary={
                                            <Typography
                                                variant='h5'
                                                component='div'
                                                sx={{
                                                    justifyContent: 'left',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    flexGrow: 1,
                                                    minWidth: 0
                                                }}
                                            >
                                                Sources
                                            </Typography>
                                        }
                                    />
                                )}
                                {isPanelExpanded && (
                                    <TooltipSpan title='Save Changes'>
                                        <IconButton
                                            sx={{
                                                justifyContent:'right',
                                                color: 'transparent',
                                                flexShrink: 0
                                            }}
                                            onClick={() => console.log('')}
                                        >
                                            <WarningIcon/>
                                        </IconButton>
                                    </TooltipSpan>
                                )}
                            </ListItemButton>
                        </ListItem>
                        <Divider/>
                    </List>
                    </Paper>
                </Box>
                <Box sx={{ width: `calc(${isPanelExpanded ? '82%' : '100% - 50px'}${isAdminInfoOpen ? ' - 30%' : ''})`, 
                    padding: '16px', transition: 'width 0.1s' }}>
                    <Paper ref={contentPaperRef} sx={{ boxShadow: "none" }}  style={{minHeight:'100%', maxHeight:'100%', 
                        maxWidth:'100%', overflow: 'auto', overflowX: 'hidden', padding: '1px'}}>
                    {selectedKey ? (
                        <div>{UCMListItem[selectedKey]()}</div>
                    ) : (
                        <div>Select an item from the list</div>
                    )}
                    <LoadingOverlay open={contentLoading} 
                        top={`${contentPaperRef?.current?.parentElement?.offsetTop || 0}px`}
                        left={`${contentPaperRef?.current?.parentElement?.offsetLeft || 0}px`}
                        width={`${contentPaperRef?.current?.parentElement?.offsetWidth || 0}px`}
                        height={`${contentPaperRef?.current?.parentElement?.offsetHeight || 0}px`}
                    />
                    </Paper>
                </Box>
                {
                    (isAdminInfoOpen && patientZcmID != 0) && <Box sx={{ width:'30%', minWidth:'30%', borderLeft: '1px solid #ccc', padding: '2px', transition: 'width 0.1s' }}>
                        <Paper sx={{ boxShadow: "none" }} style={{minHeight:'100%', maxHeight:'100%', 
                            maxWidth:'100%', overflow: 'auto', padding: '1px'}}>
                                <ZCMPatientView zcmId={patientZcmID} practiceId={client.practiceId} patient={activePatient||undefined}/>
                        </Paper>
                    </Box>
                }
            </DialogContent>
            <DialogActions>
                {footer ? (
                    <>
                        <Tooltip title={footer.tooltip}>
                            <div>{footer.text}</div>
                        </Tooltip>
                        <div style={{ flex: '1 0 0' }} />
                    </>
                ) : null}
                {readOnly ? (
                    <Button onClick={dialogButtonClose}>OK</Button>
                ) : (
                    <>
                        <Button onClick={dialogButtonClose}>Close</Button>
                        <SpinnerButton onClick={ !activePatient && activePatientId == '' ?  confirmCreatePatient.show : confirmFullSave.show } showSpinner={saving}>
                            {submitText || (!activePatient && activePatientId == '') ? 'Create Patient' : 'Save'} 
                        </SpinnerButton>
                    </>
                )}
            </DialogActions>
            {
                confirmVisitDialog.open && <SimpleDialog open={confirmVisitDialog.open} title={'Create Visit'} backdropClose={false}
                    body={`Do you want to create a new visit for patient: ${activePatient?.firstName} ${activePatient?.lastName}?`}
                    buttons={[
                        {text:'No', disabled:creatingVisit, onClick:confirmVisitDialog.hide},
                        {text:'Yes', disabled:creatingVisit, type:'spinner', busy:creatingVisit, onClick:async ()=>{
                            setCreatingVisit(true);
                            if(activeVisit?._id) {
                                syncPendingVisitChanges(activeVisit?._id, unsyncedVisitChanges[activeVisit?._id]);
                                await createVisit(activePatient?._id || '', client?._id, async (id) => { 
                                    await handleListItemClick('visits', id); 
                                } );
                            }
                            confirmVisitDialog.hide();
                            setCreatingVisit(false);
                        }}
                    ]}
                />
            }
            {
                confirmCreatePatient.open && <SimpleDialog open={confirmCreatePatient.open} title={'Create Patient'} 
                    body={
                        `Do you want to create ${activePatient?.firstName || activePatientChanges?.firstName || unsyncedPatientChanges?.firstName} `
                            + `${activePatient?.lastName || activePatientChanges?.lastName || unsyncedPatientChanges?.lastName}`
                    }
                    buttons={[
                        {text:'No', onClick:confirmCreatePatient.hide},
                        {text:'Create & Exit', onClick:()=>{
                            onSaveAndClose();
                            confirmCreatePatient.hide();
                        }},
                        {text:'Create & Continue', onClick:() => {
                            onSaveAndContinue(() => { snackSuccess('Save Successfull'); });
                            confirmCreatePatient.hide();
                        }}
                    ]}
                />
            }
            {
                confirmFullSave.open && <SimpleDialog open={confirmFullSave.open} title={'Create Patient'} 
                    body={`Do you want save all changes for ${activePatient?.firstName} ${activePatient?.lastName}?`}
                    buttons={[
                        {text:'No', onClick:confirmFullSave.hide},
                        {text:'Save & Exit', onClick:()=>{
                            onSaveAndClose();
                            confirmFullSave.hide();
                        }},
                        {text:'Save & Continue', onClick:() => {
                            onSaveAndContinue(() => { snackSuccess('Save Successfull'); });
                            confirmFullSave.hide();
                        }}
                    ]}
                />
            }
            <AlertSnack {...snackProps}/>
        </ResizableDialog>
    );
};

export default UCMEditor;